import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTheme, makeStyles } from 'pencil/styles';
import Drawer from 'pencil/Drawer';
import { withRouter } from 'circe/routing/provider';
import Grid from 'pencil/Grid';
import { useWidth } from 'domain/fuse/layout/utils';
import * as I18n from 'circe/I18n';
import { groupMapping, titleMapping } from '../constants';
import GroupSidebar from './GroupSidebar';
import ItemsSidebar from './ItemsSidebar';

const DRAWER_WIDTH = 395;
const DRAWER_CLOSE_WIDTH = 100;

const useStyles = makeStyles(
  (theme) => ({
    sidebar: {
      padding: theme.spacing(3, 0),
      // paddingLeft: 30,
      display: 'flex',
      minHeight: '100vh',
    },
    sidebarWrapper: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      height: 'fit-content',
      position: 'sticky',
      top: 0,
    },
    itemsSidebar: {
      padding: 40,
      paddingTop: 28,
    },
    hideItems: {
      display: 'none',
    },
    appIcon: {
      width: 75,
      height: 90,
      '& img': {
        objectFit: 'contain',
      },
    },
    drawer: {
      display: 'flex',
    },
    mainItems: {
      width: DRAWER_CLOSE_WIDTH,
      // padding: theme.spacing(3, 1),
      position: 'sticky',
      top: 0,
    },
    subItems: {
      width: DRAWER_WIDTH - DRAWER_CLOSE_WIDTH,
      padding: theme.spacing(3, 3),
    },
    drawerPaper: {
      width: DRAWER_WIDTH,
    },
    hide: {
      display: 'none',
    },
    drawerHeader: {
      display: 'flex',
      alignItems: 'center',
      paddingBottom: 20,
      ...theme.mixins.toolbar,
      justifyContent: 'space-between',
    },
    logo: {
      marginRight: 10,
    },
    activeLink: {
      background: theme.palette.primary.main,
      display: 'block',
      textDecoration: 'none',
      '& svg': {
        color: '#fff',
      },
    },
    link: {
      textDecoration: 'none',
      color: theme.palette.primary.contrastText,
    },
    onlyLogo: {
      marginLeft: 100,
      float: 'left',
    },
    drawerOpen: {
      width: DRAWER_WIDTH,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
      backgroundColor: theme.branding.background.secondary,
    },
    drawerClose: {
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      overflowX: 'hidden',
      width: DRAWER_CLOSE_WIDTH,
      [theme.breakpoints.up('sm')]: {
        width: DRAWER_CLOSE_WIDTH,
      },
      backgroundColor: theme.branding.background.primary,
    },
    listItem: {
      height: 38,
      borderRadius: 2,
      marginBottom: 10,
    },

    itemText: {
      fontSize: 14,
    },

    group: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 24,
      height: 24,
      marginBottom: 48,

      cursor: 'pointer',
    },
    nestedGroup: {
      width: 40,
    },
    bigIcon: {
      width: 24,
    },
    activeGroup: {
      color: theme.palette.primary.main,
    },
    headerTitle: {
      color: theme.palette.secondary.main,
      fontSize: 14,
    },
    subtitle: {
      opacity: 0.6,
      fontSize: 14,
      color: '#44465f',
      paddingBottom: 7,
    },
  }),
  { name: 'MlcGoSidebar' },
);
const cached = {};
const Sidebar = ({
  handleDrawerClose,
  handleDrawerOpen,
  authorities,
  isAllowedMenuItem,
  setCurrentPageTitle,
  currentPageTitle,
  history,
  accountToken,
  open,
}) => {
  const classes = useStyles();
  const isMobile = useWidth() === 'xs';

  const [sidebarTitle, setSidebarTitle] = useState(titleMapping.title);

  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);

  const [activeGroup, setActiveGroup] = useState('');

  function handleCompanyDialogOpen() {
    setCompanyDialogOpen(() => true);
  }

  function handleCompanyDialogClose() {
    setCompanyDialogOpen(() => false);
  }

  const toggleDrawer = (title, groupTitle) => {
    if (open) {
      handleDrawerClose();
    } else {
      handleDrawerOpen();
    }
    setSidebarTitle(titleMapping[groupTitle]);
    setCurrentPageTitle(I18n.str(title, titleMapping[title]));
  };

  const toggleGroupDrawer = (title, groupTitle) => {
    if (open && currentPageTitle == title) {
      handleDrawerClose();
    } else {
      handleDrawerOpen();
    }
    setSidebarTitle(I18n.str(groupTitle, titleMapping[groupTitle]));
  };

  const isGroupActive = (match, location, title) => {
    if (title === 'dashboard') {
      if (groupMapping[title].includes(location.pathname)) {
        return true;
      }
      return false;
    }
    if (title === 'shipping') {
      if (location.pathname.includes('update-recipient')) {
        return true;
      }
      return false;
    }
    if (title === 'settings') {
      const matches = groupMapping[title].filter((element) => {
        if (location.pathname.indexOf(element) !== -1) {
          return true;
        }
      });

      if (matches.length > 0) {
        return true;
      }
      return false;
    }
    if (title === 'drivers') {
      if (
        (location.pathname.includes('update') && location.pathname.includes('driver'))
        || location.pathname.includes('itinerary-status')
      ) {
        return true;
      }
      if (groupMapping[title].includes(location.pathname)) {
        return true;
      }
      return false;
    }
  };

  // TODO @Ahmad Yahya Check this it can be enhanced
  const isSidebarItemActiveMemoized = (match, title) => {
    if (!match) return false;
    const pageTitle = I18n.str(title, titleMapping[title]);
    if (currentPageTitle !== pageTitle) {
      setCurrentPageTitle(pageTitle);
    }
    return true;
  };
  const isSidebarItemActive = (match, title) => {
    let key = '';
    if (match) {
      key = `${match.url}/${title}`;
    } else {
      key = `empty/${title}`;
    }
    if (cached[key]) {
      return cached[key];
    }
    const results = isSidebarItemActiveMemoized(match, title);
    cached[key] = results;
    return results;
  };

  const checkSidebarSelection = (selection) => selection == sidebarTitle;

  return (
    <>
      <Drawer
        anchor="left"
        variant="permanent"
        open={open}
        ModalProps={{ onBackdropClick: handleDrawerOpen }}
        className={classNames(classes.drawer, {
          [classes.drawerOpen]: open,
          [classes.drawerClose]: !open,
        })}
        classes={{
          paper: classNames({
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          }),
        }}
      >
        <div className={classes.drawer}>
          <div className={classes.mainItems}>
            <GroupSidebar
              isAllowedMenuItem={isAllowedMenuItem}
              authorities={authorities}
              classes={classes}
              toggleDrawer={toggleDrawer}
              isGroupActive={isGroupActive}
              toggleGroupDrawer={toggleGroupDrawer}
            />
          </div>

          {open && (
            <div className={classes.subItems}>
              <div
                className={classNames({
                  [classes.hideItems]: !open,
                })}
              >
                <ItemsSidebar
                  classes={classes}
                  sidebarTitle={sidebarTitle}
                  isAllowedMenuItem={isAllowedMenuItem}
                  authorities={authorities}
                  checkSidebarSelection={checkSidebarSelection}
                  handleDrawerClose={handleDrawerClose}
                  toggleDrawer={toggleDrawer}
                  isSidebarItemActive={isSidebarItemActive}
                  open={open}
                  currentPageTitle={currentPageTitle}
                  setCurrentPageTitle={setCurrentPageTitle}
                  handleCompanyDialogOpen={handleCompanyDialogOpen}
                  companyDialogOpen={companyDialogOpen}
                  handleCompanyDialogClose={handleCompanyDialogClose}
                  accountToken={accountToken}
                  isMobile={isMobile}
                  activeGroup={activeGroup}
                />
              </div>
            </div>
          )}
        </div>
      </Drawer>
    </>
  );
};

Sidebar.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired,
};

Sidebar.propTypes = {};

export default withRouter(Sidebar);
